import { useMemo, useState } from "react";

export type SortDir = "asc" | "desc";

/**
 * Ustun uchun taqqoslash kaliti. `null` — qiymat yo'q; bunday qatorlar
 * yo'nalishdan QAT'I NAZAR oxirida qoladi (bo'sh kataklar tepani egallab
 * jadvalni foydasiz qilib qo'ymasligi kerak).
 */
export type SortValue = string | number | null;

export type Comparators<K extends string> = Record<K, (row: never) => SortValue>;

/**
 * Sarlavhaga bosib saralanadigan jadval uchun holat.
 *
 * Server tomonda saralash yo'q (`/products` faqat filtrlarni oladi) va tovar
 * ro'yxati bir so'rovda to'liq keladi — shuning uchun saralash mijoz tomonda.
 * Bir ustunga qayta bosilsa yo'nalish teskarisiga o'zgaradi.
 */
export function useTableSort<T, K extends string>(
  rows: T[],
  comparators: Record<K, (row: T) => SortValue>,
  initial?: { key: K; dir?: SortDir },
) {
  const [sortKey, setSortKey] = useState<K | null>(initial?.key ?? null);
  const [sortDir, setSortDir] = useState<SortDir>(initial?.dir ?? "asc");

  const toggle = (key: K) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    const get = comparators[sortKey];
    if (!get) return rows;
    const sign = sortDir === "asc" ? 1 : -1;
    // Barqaror saralash uchun indeks bilan birga (Array.sort barqaror, lekin
    // teng kalitlarda asl tartibni aniq saqlaymiz).
    return rows
      .map((row, i) => ({ row, i, v: get(row) }))
      .sort((a, b) => {
        if (a.v === null && b.v === null) return a.i - b.i;
        if (a.v === null) return 1; // bo'sh — doim oxirida
        if (b.v === null) return -1;
        if (typeof a.v === "number" && typeof b.v === "number") {
          return a.v === b.v ? a.i - b.i : (a.v - b.v) * sign;
        }
        const cmp = String(a.v).localeCompare(String(b.v), undefined, {
          numeric: true,
          sensitivity: "base",
        });
        return cmp === 0 ? a.i - b.i : cmp * sign;
      })
      .map((x) => x.row);
  }, [rows, sortKey, sortDir, comparators]);

  return { sorted, sortKey, sortDir, toggle };
}
